import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PaywallView } from '../components/PaywallView';
import { canGoBack } from '../lib/history';
import { Badge, Button, Markdown, Spinner, VideoCarousel } from '../components/ui';

export function IdeaDetailPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = Number(params.id);
  const valid = Number.isInteger(id) && id > 0;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['idea', id],
    queryFn: () => api.getIdea(id),
    enabled: valid,
  });

  function goBack() {
    if (canGoBack()) navigate(-1);
    else navigate(data ? `/ideas?format=${data.idea.format_code}` : '/ideas', { replace: true });
  }

  if (!valid) {
    return (
      <div className="min-h-screen p-4 max-w-lg mx-auto safe-top">
        <BackLink onClick={goBack} />
        <p className="text-center py-16 text-tg-hint">Идея не найдена</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen p-4 max-w-lg mx-auto safe-top">
        <BackLink onClick={goBack} />
        <div className="flex flex-col items-center py-16">
          <Spinner />
          <p className="text-sm text-tg-hint mt-3">Загружаем идею</p>
        </div>
      </div>
    );
  }

  if (error) {
    if (error instanceof ApiError && error.status === 402) {
      return <PaywallView />;
    }
    const is404 = error instanceof ApiError && error.status === 404;
    return (
      <div className="min-h-screen p-4 max-w-lg mx-auto safe-top">
        <BackLink onClick={goBack} />
        <div className="text-center py-16">
          <p className="text-sm text-tg-hint mb-4">
            {is404 ? 'Идея не найдена' : 'Не удалось загрузить идею'}
          </p>
          {!is404 && (
            <Button onClick={() => refetch()} variant="primary">
              Повторить
            </Button>
          )}
        </div>
      </div>
    );
  }

  const idea = data!.idea;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-8 safe-top">
      <BackLink onClick={goBack} />

      {idea.preview_video_urls.length > 0 && (
        <VideoCarousel urls={idea.preview_video_urls} className="mb-4" />
      )}

      <h1 className="text-xl font-bold mb-3 leading-tight">{idea.title}</h1>

      <div className="flex flex-wrap gap-1.5 mb-4">
        <Badge tone="brand">{idea.matryoshka}</Badge>
        {idea.marketing_goals.map((goal) => (
          <Badge key={goal.id} tone="neutral">
            {goal.label}
          </Badge>
        ))}
      </div>

      <section className="mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-tg-hint mb-1.5">
          Описание идеи
        </h2>
        <Markdown>{idea.description}</Markdown>
      </section>

      {idea.audience_action && (
        <section className="mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-tg-hint mb-1.5">
            Действие аудитории
          </h2>
          <p className="text-sm leading-relaxed">{idea.audience_action}</p>
        </section>
      )}

      <Button
        onClick={() => navigate(`/sessions/new?idea_id=${idea.id}`)}
        variant="primary"
        size="lg"
        fullWidth
        leftIcon={<Sparkles size={18} />}
        className="mt-6"
      >
        {idea.format_code === 'carousel' ? 'Создать карусель' : 'Использовать эту идею'}
      </Button>
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors mb-4"
    >
      <ArrowLeft size={16} />
      Назад
    </button>
  );
}
